import _get from 'lodash/get';

import { pageNames } from '@atc/bonnet-paths';

import getScaledPrimaryImg from '@/utilities/getScaledPrimaryImg';
import getSRPPrimaryImagesFromCtx from '@/utilities/getSRPPrimaryImagesFromCtx';

const { VEHICLE_DETAILS, SEARCH_RESULTS } = pageNames;

/**
 * Verifies and updates image source URL for optimization if enabled
 * @param {string} imageSrc - Original image source URL
 * @param {boolean} enableImageOptimization - Flag to enable image optimization
 * @returns {string} - Verified image source URL
 */
export function getVerifiedImageSrc(imageSrc, enableImageOptimization) {
    if (!imageSrc) return '';

    return enableImageOptimization
        ? imageSrc.replace(/images.autotrader.com|assets.cai-media-management.com/, 'images2.autotrader.com')
        : imageSrc;
}

/**
 * Extracts primary image source from VDP data
 * @param {Object} data - VDP data object
 * @returns {string} - Primary image source URL
 */
export function getVDPPrimaryImageSrc(data) {
    const images = _get(data, 'listings[0].images', {});
    const primary = _get(images, 'primary', 0);
    return _get(images, `sources[${primary}].src`, '');
}

/**
 * Extracts primary image source from SRP data
 * @param {Object} ctx - Context object
 * @returns {string} - Primary image source URL
 */
export function getSRPPrimaryImageSrc(ctx) {
    const primaryImages = getSRPPrimaryImagesFromCtx(ctx);
    return _get(primaryImages, 'sources[0].src', '');
}

/**
 * Processes VDP image and returns scaled primary image if applicable
 * @param {Object} ctx - Context object
 * @param {Object} browser - Browser information
 * @param {boolean} enableImageOptimization - Flag to enable image optimization
 * @returns {string|null} - Scaled primary image URL or null
 */
export function processVDPImage(ctx, browser, enableImageOptimization) {
    const primaryImgSrc = getVDPPrimaryImageSrc(ctx.data);

    // Only add the preload on the vdp for mobile, otherwise we can't predictably know which size the
    // primary image will be for the responsive image on the vdp.  We'll have to rely on a link preload
    // in the head with the responsive srcset for desktop.
    if (!primaryImgSrc || !_get(browser, 'is.xs')) {
        return null;
    }
    const verifiedImgSrc = getVerifiedImageSrc(primaryImgSrc, enableImageOptimization);
    return getScaledPrimaryImg(VEHICLE_DETAILS, browser, verifiedImgSrc, { isOptimized: enableImageOptimization });
}

/**
 * Processes SRP image and returns scaled primary image if applicable
 * @param {Object} ctx - Context object
 * @param {Object} browser - Browser information
 * @param {boolean} enableImageOptimization - Flag to enable image optimization
 * @returns {string|null} - Scaled primary image URL or null
 */
export function processSRPImage(ctx, browser, enableImageOptimization) {
    const primaryImageSrc = getSRPPrimaryImageSrc(ctx);

    if (!primaryImageSrc) {
        return null;
    }

    const verifiedImgSrc = getVerifiedImageSrc(primaryImageSrc, enableImageOptimization);
    return getScaledPrimaryImg(SEARCH_RESULTS, browser, verifiedImgSrc, { isOptimized: enableImageOptimization });
}

/* preload(http2 server push) the primary image */
export default function serverPushKeyRequests() {
    return async (ctx) => {
        // Early return for client-side execution
        if (typeof window !== 'undefined') {
            return;
        }

        const { store, res } = ctx;
        const { browser } = store.getState();

        const {
            image_optimization: [enableImageOptimization],
        } = ctx.useFeatures([
            'image_optimization',
        ]);

        let scaledPrimaryImage = null;

        if (ctx.pageName === VEHICLE_DETAILS) {
            scaledPrimaryImage = processVDPImage(ctx, browser, enableImageOptimization);
        } else if (ctx.pageName === SEARCH_RESULTS) {
            scaledPrimaryImage = processSRPImage(ctx, browser, enableImageOptimization);
        }

        if (scaledPrimaryImage) {
            const preloads = [`<${scaledPrimaryImage}>; rel=preload; as=image`];
            res.setHeader('Link', preloads);
        }
    };
}
